import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";
import { Link } from "react-router-dom";
import { FAITHS } from "@/lib/lunelle-data";
import { LogoMark } from "./Logo";
import { InviteCard } from "./InviteCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WaitlistForm = ({ prefix = "hero", dark = false, onJoined }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [faith, setFaith] = useState("spiritual");
  const [consent, setConsent] = useState(false);
  const [position, setPosition] = useState(null);
  const [invite, setInvite] = useState(null);
  const [website, setWebsite] = useState("");
  const mountedAt = useRef(Date.now());

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to join");
      return;
    }
    setState("loading");
    try {
      const data = await joinWaitlist(email, prefix, faith, { website, elapsed_ms: Date.now() - mountedAt.current });
      setState("done");
      setPosition(data?.position ?? null);
      setInvite(data?.referral_code ? { code: data.referral_code, referrals: data?.referrals ?? 0, spots: data?.spots_per_friend ?? 5 } : null);
      onJoined?.(data?.count);
      const spot = data?.position ? `You're #${data.position.toLocaleString()}` : null;
      toast.success(data?.already ? `You're already on the list${spot ? ` — #${data.position.toLocaleString()}` : ""}` : spot ? `${spot} — welcome to Lunelle` : "Welcome to Lunelle", {
        description: data?.already ? "We'll write when your first season begins." : "Check your inbox and tap “Confirm my email” to secure your spot.",
      });
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.status === 429 ? err.response.data?.detail ?? "Too many attempts. Please try later." : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-md">
    <form onSubmit={submit} data-testid={`${prefix}-waitlist-form`}
      className={`flex w-full items-center gap-2 rounded-full border p-1.5 pl-5 backdrop-blur-md ${
        dark ? "border-white/20 bg-white/5" : "border-[#C86D51]/25 bg-white/60"
      }`}>
      <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
        aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid={`${prefix}-waitlist-hp`} />
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
        data-testid={`${prefix}-waitlist-email-input`} disabled={state === "done"}
        className={`min-w-0 flex-1 bg-transparent text-sm outline-none ${dark ? "text-[#FBF5EE] placeholder:text-[#d9c7bd]" : "text-[#2C221E] placeholder:text-[#8a766c]"}`}
      />
      <motion.button
        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} type="submit"
        data-testid={`${prefix}-waitlist-submit-button`} disabled={state !== "idle"}
        className="flex shrink-0 items-center gap-2 rounded-full bg-[#9E4429] px-5 py-3 text-sm font-semibold text-[#FBF5EE] transition-colors duration-300 hover:bg-[#843722]"
      >
        {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
        {state === "done" && <Check className="h-4 w-4" />}
        {state === "done" ? "You're in" : "Join waitlist"}
        {state === "idle" && <ArrowRight className="h-4 w-4" />}
      </motion.button>
    </form>
    {state === "done" && position ? (
      <>
        <FoundingBadge position={position} prefix={prefix} dark={dark} />
        {invite && <InviteCard code={invite.code} referrals={invite.referrals} spots={invite.spots} prefix={prefix} dark={dark} />}
      </>
    ) : (
    <>
    <div className={`mt-3 flex items-center gap-2 pl-5 text-xs ${dark ? "text-[#d9c7bd]" : "text-[#6B5A52]"}`}>
      <span>Daily word from my faith:</span>
      <Select value={faith} onValueChange={setFaith} disabled={state === "done"}>
        <SelectTrigger data-testid={`${prefix}-waitlist-faith-select`} className="h-8 w-auto gap-2 rounded-full border-[#C86D51]/25 bg-transparent px-3 text-xs font-semibold text-[#9E4429]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-2xl bg-[#FDFBF7]">
          {FAITHS.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`${prefix}-waitlist-faith-option-${f.id}`}>{f.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <label className={`mt-3 flex cursor-pointer items-start gap-2.5 pl-5 text-xs leading-relaxed ${dark ? "text-[#d9c7bd]" : "text-[#6B5A52]"}`}>
      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={state === "done"}
        data-testid={`${prefix}-waitlist-consent-checkbox`} className="mt-0.5 h-4 w-4 shrink-0 accent-[#9E4429]" />
      <span>
        I agree to receive Lunelle emails and for my faith choice to be stored to personalise them. Leave anytime.{" "}
        <Link to="/privacy" className="font-semibold text-[#9E4429] underline underline-offset-2" data-testid={`${prefix}-waitlist-privacy-link`}>Privacy policy</Link>
      </span>
    </label>
    </>
    )}
    </div>
  );
};

const FoundingBadge = ({ position, prefix, dark }) => (
  <motion.div
    initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ type: "spring", stiffness: 220, damping: 20 }}
    className={`mt-4 flex items-center gap-4 rounded-2xl border p-4 ${dark ? "border-white/15 bg-white/5" : "border-[#C86D51]/20 bg-[#FBF5EE]"}`}
    data-testid={`${prefix}-founding-badge`}
  >
    <motion.span initial={{ rotate: -90 }} animate={{ rotate: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#9E4429]">
      <LogoMark className="h-7 w-7" color="#FBF5EE" />
    </motion.span>
    <span>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9E4429]">Founding member</span>
      <span className={`block font-serif text-3xl leading-tight ${dark ? "text-[#FBF5EE]" : "text-[#2C221E]"}`} data-testid={`${prefix}-founding-position`}>
        You're #{position.toLocaleString()}
      </span>
    </span>
  </motion.div>
);
